import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Panel } from 'primereact/panel';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Dropdown } from 'primereact/dropdown';
import { Message } from 'primereact/message';
import { accountsApi, dataSourcesApi, quickbooksApi } from '../api';
import { Account, DataSource } from '../types';
import { formatDate } from '../utils';
import { useClientJobs, triggerJobsRefresh } from '../hooks';

const ORIGIN_TYPE_METADATA: Record<string, { icon: string; label: string }> = {
  file_upload: { icon: 'ms-out ms-out-upload-file', label: 'File Upload' },
  api_pull: { icon: 'ms-out ms-out-cloud-sync', label: 'API Pull' },
  manual: { icon: 'ms-out ms-out-edit-square', label: 'Manual Entry' },
  system: { icon: 'ms-out ms-out-settings', label: 'System Process' },
  unknown: { icon: 'ms-out ms-out-help', label: 'Unknown' },
};

export const DataAcquisition: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const toast = useRef<Toast>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // State
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(true);
  const [isLoadingDataSources, setIsLoadingDataSources] = useState(true);

  // Account selection dialog
  const [showAccountDialog, setShowAccountDialog] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [pendingUploadType, setPendingUploadType] = useState<'bank_statement' | 'customer_deposits' | null>(null);

  // Error state
  const [error, setError] = useState<string | null>(null);

  // QuickBooks
  const [qbLinked, setQbLinked] = useState(false);
  const [qbCompanyName, setQbCompanyName] = useState<string | null>(null);
  const [isCheckingQbStatus, setIsCheckingQbStatus] = useState(false);
  const [isSyncingQb, setIsSyncingQb] = useState(false);

  // Track active jobs for this client to disable buttons and refresh on completion
  const { isJobTypeRunning, hasActiveJobs } = useClientJobs(clientId);
  const prevHasActiveJobsRef = useRef<boolean>(false);

  useEffect(() => {
    if (clientId) {
      loadAccounts();
      loadDataSources();
      checkQuickBooksStatus();
    }
  }, [clientId]);

  // Refresh table when all jobs complete
  useEffect(() => {
    if (prevHasActiveJobsRef.current && !hasActiveJobs) {
      loadDataSources();
    }
    prevHasActiveJobsRef.current = hasActiveJobs;
  }, [hasActiveJobs]);

  const loadAccounts = async () => {
    if (!clientId) return;
    try {
      setIsLoadingAccounts(true);
      const data = await accountsApi.listAccounts(clientId);
      setAccounts(data);
      if (data.length > 0 && !selectedAccountId) {
        setSelectedAccountId(data[0].id);
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.detail || 'Failed to load accounts',
        life: 5000,
      });
    } finally {
      setIsLoadingAccounts(false);
    }
  };

  const loadDataSources = async () => {
    if (!clientId) return;
    try {
      setIsLoadingDataSources(true);
      const data = await dataSourcesApi.listDataSources(clientId);
      setDataSources(data);
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.detail || 'Failed to load data sources',
        life: 5000,
      });
    } finally {
      setIsLoadingDataSources(false);
    }
  };

  const checkQuickBooksStatus = async () => {
    if (!clientId) return;
    try {
      setIsCheckingQbStatus(true);
      const status = await quickbooksApi.getLinkStatus(clientId);
      setQbLinked(status.linked);
      setQbCompanyName(status.company_name);
    } catch (err: any) {
      console.error('Failed to check QuickBooks status:', err);
    } finally {
      setIsCheckingQbStatus(false);
    }
  };

  // Menubar action handlers
  const handleQbSync = async () => {
    if (!clientId) return;

    setError(null);
    setIsSyncingQb(true);

    try {
      // Call the actual sync API
      const result = await quickbooksApi.syncTransactions(clientId);

      // Build success message with counts
      const details = [];
      if (result.imported_count > 0) {
        details.push(`${result.imported_count} imported`);
      }
      if (result.skipped_count > 0) {
        details.push(`${result.skipped_count} skipped (already exist)`);
      }
      if (result.errors.length > 0) {
        // Log full errors to console for debugging
        console.error('QuickBooks sync errors:', result.errors);
        // Show first error in toast for visibility
        details.push(`Errors: ${result.errors[0]}${result.errors.length > 1 ? ` (+${result.errors.length - 1} more)` : ''}`);
      }

      // Add customer stats
      if (result.customer_stats) {
        const cs = result.customer_stats;
        const customerParts = [];
        if (cs.imported > 0) customerParts.push(`${cs.imported} new`);
        if (cs.linked > 0) customerParts.push(`${cs.linked} linked`);
        if (cs.updated > 0) customerParts.push(`${cs.updated} updated`);
        if (customerParts.length > 0) {
          details.push(`Customers: ${customerParts.join(', ')}`);
        }
      }

      // Add vendor stats
      if (result.vendor_stats) {
        const vs = result.vendor_stats;
        const vendorParts = [];
        if (vs.imported > 0) vendorParts.push(`${vs.imported} new`);
        if (vs.linked > 0) vendorParts.push(`${vs.linked} linked`);
        if (vs.updated > 0) vendorParts.push(`${vs.updated} updated`);
        if (vendorParts.length > 0) {
          details.push(`Vendors: ${vendorParts.join(', ')}`);
        }
      }

      toast.current?.show({
        severity: result.errors.length > 0 ? 'warn' : 'success',
        summary: 'QuickBooks Sync Complete',
        detail: details.length > 0 ? details.join('. ') : 'No new transactions found',
        life: 5000,
      });

      // Reload accounts and data sources
      await loadAccounts();
      await loadDataSources();
    } catch (err: any) {
      setError(err.message || 'Failed to sync QuickBooks ledger');
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.detail || 'Failed to sync QuickBooks',
        life: 5000,
      });
    } finally {
      setIsSyncingQb(false);
    }
  };

  const handleBankStatementSelect = () => {
    if (accounts.length === 0) {
      toast.current?.show({
        severity: 'warn',
        summary: 'No Accounts',
        detail: 'Please sync QuickBooks first to create accounts before uploading files.',
        life: 5000,
      });
      return;
    }
    setPendingUploadType('bank_statement');
    setShowAccountDialog(true);
  };

  const handleCustomerDepositsSelect = () => {
    if (accounts.length === 0) {
      toast.current?.show({
        severity: 'warn',
        summary: 'No Accounts',
        detail: 'Please sync QuickBooks first to create accounts before uploading files.',
        life: 5000,
      });
      return;
    }
    setPendingUploadType('customer_deposits');
    setShowAccountDialog(true);
  };

  const handleAccountDialogConfirm = () => {
    if (!selectedAccountId) {
      setError('Please select an account to continue');
      return;
    }

    setShowAccountDialog(false);
    openFileDialog(selectedAccountId);
  };

  const handleAccountDialogCancel = () => {
    setShowAccountDialog(false);
    setPendingUploadType(null);
  };

  const openFileDialog = (accountId: string | null) => {
    if (fileInputRef.current) {
      fileInputRef.current.dataset.accountId = accountId || '';
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !clientId) return;

    const accountId = event.target.dataset.accountId || selectedAccountId;
    if (!accountId) {
      setError('Please select an account before uploading');
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'An account must be selected before uploading files',
        life: 5000,
      });
      return;
    }
    setError(null);

    const uploadedSourceIds: string[] = [];

    try {
      const total = files.length;

      // Upload all files
      for (let i = 0; i < total; i++) {
        const file = files[i];

        const formData = new FormData();
        formData.append('file', file);
        formData.append('account_id', accountId);
        if (pendingUploadType) {
          formData.append('source_type', pendingUploadType);
        }

        const uploadedSource = await dataSourcesApi.uploadFile(clientId, formData);

        // Note: uploadFile returns FileUploadResponse with data_source_id (not id)
        const sourceId = (uploadedSource as any).data_source_id || uploadedSource.id;
        if (sourceId) {
          uploadedSourceIds.push(sourceId);
        }
      }

      // Start parse jobs for uploaded files concurrently
      if (pendingUploadType && uploadedSourceIds.length > 0) {
        await Promise.all(
          uploadedSourceIds.map((sourceId) =>
            dataSourcesApi.parseFile(clientId, sourceId, pendingUploadType).catch((parseErr) => {
              console.error('Failed to start parse job:', parseErr);
            })
          )
        );
        // Trigger immediate refresh of job indicators
        triggerJobsRefresh();
      }

      await loadDataSources();
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: `${total} file(s) uploaded and processing started`,
        life: 3000,
      });
    } catch (err: any) {
      const isDuplicate =
        err.response?.status === 409 &&
        err.response?.data?.detail?.includes('already been uploaded');

      if (isDuplicate) {
        toast.current?.show({
          severity: 'warn',
          summary: 'Duplicate File',
          detail:
            'A bank statement with this filename has already been uploaded. Delete the existing file first to re-upload.',
          life: 5000,
        });
      } else {
        setError(err.message || 'Failed to upload files');
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: err.response?.data?.detail || 'Failed to upload file',
          life: 5000,
        });
      }
    } finally {
      setPendingUploadType(null);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteDataSource = (dataSource: DataSource) => {
    if (!clientId) return;
    confirmDialog({
      message: `Are you sure you want to delete "${dataSource.original_filename}"?`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await dataSourcesApi.deleteDataSource(clientId, dataSource.id);
          setDataSources(dataSources.filter((ds) => ds.id !== dataSource.id));
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Data source deleted successfully',
            life: 3000,
          });
        } catch (err: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: err.response?.data?.detail || 'Failed to delete data source',
            life: 5000,
          });
        }
      },
    });
  };

  const handleReprocessFile = async (dataSource: DataSource) => {
    if (!clientId) return;

    try {
      setError(null);

      // Map source_type to parse_type
      const parseType = dataSource.source_type === 'check_images' ? 'checks' : (dataSource.source_type || 'bank_statement');
      await dataSourcesApi.parseFile(clientId, dataSource.id, parseType);

      toast.current?.show({
        severity: 'info',
        summary: 'Started',
        detail: 'File reprocessing started',
        life: 3000,
      });
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.detail || 'Failed to reprocess file',
        life: 5000,
      });
    }
  };


  const accountDropdownOptions = accounts.map((account) => ({
    label: account.name,
    value: account.id,
  }));

  // Table body templates
  const sourceNameBodyTemplate = (rowData: DataSource) => {
    return rowData.original_filename || rowData.name || '—';
  };

  const dateBodyTemplate = (rowData: DataSource) => {
    return formatDate(rowData.created_at) || '—';
  };

  const sourceTypeBodyTemplate = (rowData: DataSource) => {
    const label = rowData.source_type || 'unknown';
    return label.replace(/_/g, ' ');
  };

  const accountBodyTemplate = (rowData: DataSource) => {
    if (!rowData.account_id) return '—';
    const account = accounts.find((a) => a.id === rowData.account_id);
    return account?.name || '—';
  };

  const originBodyTemplate = (rowData: DataSource) => {
    const originTypeKey = (rowData.origin_type || 'unknown').toLowerCase();
    const originMeta = ORIGIN_TYPE_METADATA[originTypeKey] || ORIGIN_TYPE_METADATA.unknown;
    const systemLabel = rowData.origin_system || 'Unknown source';
    const normalizedTypeLabel = originTypeKey.replace(/\s+/g, '-');
    const iconName =
      normalizedTypeLabel === 'api' || normalizedTypeLabel === 'api-pull'
        ? 'ms-out ms-out-cloud'
        : normalizedTypeLabel === 'file'
          ? 'ms-out ms-out-upload-file'
          : originMeta.icon;

    return (
      <div>
        <span className={iconName} aria-hidden="true"></span>
        <span className="value-with-icon">{systemLabel}</span>
      </div>
    );
  };

  const fileSizeBodyTemplate = (rowData: DataSource) => {
    if (!rowData.file_size_bytes) return '—';
    const kb = rowData.file_size_bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  const pageCountBodyTemplate = (rowData: DataSource) => {
    if (!rowData.page_count) return '—';
    return rowData.page_count;
  };

  const processingDurationBodyTemplate = (rowData: DataSource) => {
    const start = rowData.processing_started_at ? new Date(rowData.processing_started_at) : null;
    const end = rowData.processing_completed_at ? new Date(rowData.processing_completed_at) : null;

    if (!start && !end) return '—';
    if (start && !end) return 'In progress';
    if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '—';

    const diffMs = Math.max(0, end.getTime() - start.getTime());
    const seconds = Math.floor(diffMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      const remainingMinutes = minutes % 60;
      return `${hours}h ${remainingMinutes}m`;
    }
    if (minutes > 0) {
      const remainingSeconds = seconds % 60;
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${seconds}s`;
  };

  const statusBodyTemplate = (rowData: DataSource) => {
    const statusMap: Record<string, { severity: 'success' | 'info' | 'warn' | 'error'; text: string }> = {
      uploaded: { severity: 'warn', text: 'Processing' },
      pending: { severity: 'warn', text: 'Processing' },
      processing: { severity: 'warn', text: 'Processing' },
      processed: { severity: 'success', text: 'Processed' },
      completed: { severity: 'success', text: 'Completed' },
      error: { severity: 'error', text: 'Error' },
      failed: { severity: 'error', text: 'Failed' },
    };

    const ingestStatus = rowData.ingest_status || 'pending';
    const status = statusMap[ingestStatus] || { severity: 'info', text: ingestStatus };
    return <Message severity={status.severity} text={status.text} />;
  };

  const actionsBodyTemplate = (rowData: DataSource) => {
    return (
      <div className="row-actions">
        <Button
          icon="ms-out ms-out-refresh"
          link
          tooltip="Reprocess"
          tooltipOptions={{ position: 'bottom' }}
          onClick={() => handleReprocessFile(rowData)}
        />
        <Button
          icon="ms-out ms-out-delete"
          link
          tooltip="Delete"
          tooltipOptions={{ position: 'bottom' }}
          onClick={() => handleDeleteDataSource(rowData)}
        />
      </div>
    );
  };

  // Account dialog footer
  const accountDialogFooter = (
    <div>
      <Button
        label="Cancel"
        onClick={handleAccountDialogCancel}
      />
      <Button
        label="Continue"
        className="p-button-success"
        onClick={handleAccountDialogConfirm}
        disabled={!selectedAccountId}
      />
    </div>
  );

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog />

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".pdf,.csv,.xlsx,.xls,.jpg,.jpeg,.png"
        multiple
        hidden
      />

      <Panel>
        <div className="file-input-activity-container">
          <div className="controls-panel fancy">
            {qbLinked && (
              <Button
                className="fancy-control"
                label="Download QB Data"
                link
                icon="ms-out ms-out-cloud-download"
                onClick={handleQbSync}
                loading={isSyncingQb}
                disabled={isSyncingQb}
              />
            )}
            <Button
              className="fancy-control"
              label="Upload Bank Statement(s)"
              icon="ms-out ms-out-upload"
              link
              onClick={handleBankStatementSelect}
            />
            <Button
              className="fancy-control"
              label="Upload Customer Deposits"
              link
              icon="ms-out ms-out-attach-money"
              onClick={handleCustomerDepositsSelect}
              disabled={isJobTypeRunning('parse_deposits')}
              tooltip={isJobTypeRunning('parse_deposits') ? 'Customer deposits are already being processed' : undefined}
              tooltipOptions={{ position: 'bottom' }}
            />
          </div>

          {error && <Message severity="error" text={error} />}
        </div>

        <DataTable
          value={dataSources}
          dataKey="id"
          loading={isLoadingDataSources}
          emptyMessage="No data sources yet. Upload bank statements or sync QuickBooks to get started."
          paginator
          rows={50}
          rowsPerPageOptions={[25, 50, 100]}
          paginatorTemplate={{
            layout: 'RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport',
            CurrentPageReport: (options) => (
              <span className="paginator-current">
                Showing {options.first}-{options.last} of <b>{options.totalRecords}</b> sources
              </span>
            ),
          }}
          size="small"
          stripedRows
          scrollable
          scrollHeight="calc(100vh - 340px)"
          sortMode="single"
        >
          <Column
            field="original_filename"
            header="Source"
            sortable
            style={{ width: '18%' }}
            body={sourceNameBodyTemplate} />
          <Column
            field="account_id"
            header="Account"
            sortable
            style={{ width: '18%' }}
            body={accountBodyTemplate} />            
          <Column
            field="created_at"
            header="Date"
            sortable
            style={{ width: '8%' }}
            body={dateBodyTemplate} />
          <Column
            field="source_type"
            header="Type"
            sortable
            style={{ width: '10%' }}
            body={sourceTypeBodyTemplate} />

          <Column
            header="Origin"
            style={{ width: '8%' }}
            body={originBodyTemplate} />
          <Column
            field="page_count"
            header="Pages"
            sortable
            style={{ width: '6%' }}
            body={pageCountBodyTemplate} />
          <Column
            field="file_size_bytes"
            header="Size"
            sortable
            style={{ width: '8%' }}
            body={fileSizeBodyTemplate} />
          <Column
            header="Duration"
            style={{ width: '8%' }}
            sortable
            body={processingDurationBodyTemplate} />
          <Column
            field="ingest_status"
            header="Status"
            sortable
            style={{ width: '10%' }}
            body={statusBodyTemplate} />
          <Column body={actionsBodyTemplate} />
        </DataTable>

        {dataSources.length > 0 && (
          <div className="table-totals">
            <div className="summary-item">
              <span className="summary-label">Total Sources:</span>
              <span className="summary-value">{dataSources.length}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Total Pages:</span>
              <span className="summary-value">
                {dataSources.reduce((sum, ds) => sum + (ds.page_count || 0), 0)}
              </span>
            </div>
          </div>
        )}
      </Panel>

      {/* Account Selection Dialog */}
      <Dialog
        header="Choose an account"
        visible={showAccountDialog}
        onHide={handleAccountDialogCancel}
        footer={accountDialogFooter}
        modal
        className="account-dialog"
      >
        <div className="message-box">
          <p>Select an account to associate with the data you're uploading.</p>
        </div>

        <div className="field">
          <Dropdown
            id="existing-account"
            value={selectedAccountId}
            options={accountDropdownOptions}
            onChange={(e: { value: string }) => setSelectedAccountId(e.value)}
            placeholder="Select an account"
            className="full-width-input"
          />
        </div>
      </Dialog>
    </>
  );
};

export default DataAcquisition;
